import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { useTranslation } from 'hooks/useTranslation';
import axiosServices, { fetcher } from 'utils/axios';
import { buildLatestReservationPath } from 'utils/scheduleReadyRedirect';

import type { ReservationHistoryResponce } from 'types/reservation';

const endpoints = {
  latest: '/reservations/latest',
  history: '/reservations/history',
  waiterReservations: '/waiters'
};

// ==============================|| GET RESERVATION BY ID HOOK ||============================== //

export function useGetReservation(reservationId: string | number | null) {
  const key = reservationId ? `/reservations/${reservationId}` : null;

  const { data, error, isLoading, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return {
    reservation: data?.data,
    reservationLoading: isLoading,
    reservationError: error,
    refetch: mutate
  };
}

// ==============================|| GET LAST RESERVATION HOOK ||============================== //

interface LastReservationResponse {
  success: boolean;
  message: string;
  data?: {
    type: string;
    id: string;
    attributes: {
      id: number;
      user_id: number;
      restaurant_id: number;
      waiter_id?: number;
      covermanager_reservation_id?: string;
      party_size: number;
      start_at: string;
      end_at: string;
      status: string;
      source?: string;
      phone_snapshot?: string;
      name_snapshot?: string;
      cm_table_id?: string;
      table_id?: number;
      table_number?: string;
      created_at: string;
      updated_at: string;
    };
  };
}

export function useGetLastReservation() {
  const [isLoading, setIsLoading] = useState(false);
  const getLastReservation = useCallback(
    async (restaurantId: number, waitlistEntryId?: string | number | null): Promise<LastReservationResponse> => {
      setIsLoading(true);
      try {
        const response = await axiosServices.get(buildLatestReservationPath(restaurantId, waitlistEntryId));

        return {
          success: true,
          message: 'Last reservation fetched successfully',
          data: response.data.data
        };
      } catch (error: any) {
        console.error('Error fetching last reservation:', error);
        return {
          success: false,
          message: error?.response?.data?.message || error?.message || 'Failed to fetch last reservation'
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { getLastReservation, isLoading };
}

// ==============================|| GET RESERVATIONS HISTORY HOOK ||============================== //

interface GetReservationsHistoryParams {
  params: {
    eligible_only?: boolean;
    restaurant_id?: number;
    page?: number;
    per_page?: number;
    since?: string;
    until?: string;
    status?: 'booked' | 'cancelled' | 'no_show';
  };
}

export function useGetReservationsHistory({ params }: GetReservationsHistoryParams) {
  const { since, until, restaurant_id, per_page = 15, status = 'booked' } = params;

  const getKey = (pageIndex: number, previousPageData: ReservationHistoryResponce | null) => {
    if (previousPageData) {
      const { meta } = previousPageData;
      const totalPages = Math.ceil(meta.total / per_page);
      if (pageIndex >= totalPages) return null;
    }

    const queryParams = new URLSearchParams();

    // if (eligible_only !== undefined) queryParams.set('eligible_only', eligible_only ? '1' : '0');
    if (restaurant_id !== undefined) queryParams.set('restaurant_id', restaurant_id.toString());
    if (since !== undefined) queryParams.set('since', since);
    if (until !== undefined) queryParams.set('until', until);
    if (status !== undefined) queryParams.set('status', status);
    queryParams.set('page', (pageIndex + 1).toString());
    queryParams.set('per_page', per_page.toString());

    return `${endpoints.history}?${queryParams.toString()}`;
  };

  const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    initialSize: 1,
    dedupingInterval: 5000,
    focusThrottleInterval: 10000,
    errorRetryInterval: 5000,
    errorRetryCount: 5
  });

  const allReservations = useMemo(() => {
    if (!data) return [];

    return data.flatMap((pageData) => pageData?.data || []);
  }, [data]);

  const memoizedValue = useMemo(() => {
    const firstPage = data?.[0];
    const meta = firstPage?.meta;

    const totalPages = meta ? Math.ceil(meta.total / per_page) : 0;
    const hasMorePages = firstPage && meta ? size < totalPages : false;

    return {
      reservations: allReservations,
      // reservations: [
      //   {
      //     id: 20,
      //     restaurant: {
      //       id: 2,
      //       name: 'Demo Restaurant'
      //     },
      //     start_at: '2025-09-03T18:30:00.000000Z',
      //     end_at: '2025-09-03T20:30:00.000000Z',
      //     people: 6,
      //     assigned_waiter: {
      //       id: 4,
      //       name: 'Maria Garcia',
      //       avatar_url: null
      //     },
      //     is_rated: false,
      //     can_review: false
      //   }
      // ],
      reservationsLoading: isLoading,
      reservationsError: error,
      reservationsValidating: isValidating,
      reservationsEmpty: !isLoading && allReservations.length === 0,
      pagination: {
        currentPage: size,
        totalPages: totalPages,
        totalItems: meta?.total || 0,
        perPage: per_page
      },
      loadMore: () => setSize(size + 1),
      hasMore: hasMorePages
    };
  }, [allReservations, data, error, isLoading, isValidating, size, setSize, per_page]);

  return memoizedValue;
}

// ==============================|| Select Waiter HOOK ||============================== //

interface ReserveWaiterParams {
  reservation_id: number;
  waiter_id: number;
}

interface ReserveWaiterResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useReserveWaiter() {
  const selectWaiter = async (params: ReserveWaiterParams): Promise<ReserveWaiterResponse> => {
    try {
      const { reservation_id, waiter_id } = params;

      const response = await axiosServices.patch(`/reservations/${reservation_id}/select-waiter`, { waiter_id });

      return {
        success: response.data.data.assignment_status === 'assigned',
        message: response.data.data.assignment_status === 'assigned' ? 'Waiter booked successfully' : 'Please select another waiter',
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error booking waiter:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to book waiter'
      };
    }
  };

  return { selectWaiter };
}

export function useSaveWaiterPreference() {
  const saveWaiterPreference = async (params: ReserveWaiterParams): Promise<ReserveWaiterResponse> => {
    try {
      const response = await axiosServices.put(`/reservations/${params.reservation_id}/waiter-preference`, {
        waiter_id: params.waiter_id
      });

      return {
        success: response.data.data?.status === 'new',
        message: 'Waiter preference saved successfully',
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error saving waiter preference:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to save waiter preference'
      };
    }
  };

  return { saveWaiterPreference };
}

interface ScheduleWaitlistContinuationResponse {
  success: boolean;
  message: string;
  data?: {
    restaurant_id: number;
    restaurant_slug: string;
    timezone: string;
    date: string;
    time: string;
    party_size: number;
  };
}

export function useGetScheduleWaitlistContinuation() {
  const [isLoading, setIsLoading] = useState(false);

  const getScheduleWaitlistContinuation = useCallback(async (entryId: string | number): Promise<ScheduleWaitlistContinuationResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.get(`/schedule-waitlist/${entryId}/continuation`);

      return {
        success: true,
        message: 'Schedule waitlist continuation fetched successfully',
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error fetching schedule waitlist continuation:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to fetch schedule waitlist continuation'
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { getScheduleWaitlistContinuation, isLoading };
}

// ==============================|| CHANGE WAITER ASSIGNMENT HOOK ||============================== //

interface ChangeWorkerParams {
  reservation_id: string | number;
  waiter_id: number;
  table_ids?: number[];
  date?: string;
}

interface ChangeWorkerResponse {
  success: boolean;
  message: string;
  data?: {
    match: boolean;
    reservation?: any;
    waiter?: any;
    tables?: any[];
  };
}
// TODO: uninte with useReserveWaiter or change endpoint
export function useChangeWorker() {
  const [isLoading, setIsLoading] = useState(false);

  const changeWaiter = async (params: ChangeWorkerParams): Promise<ChangeWorkerResponse> => {
    setIsLoading(true);
    try {
      const { reservation_id, waiter_id, table_ids, date } = params;

      const requestData: any = { waiter_id };
      if (table_ids && table_ids.length > 0) {
        requestData.table_ids = table_ids;
      }
      if (date) {
        requestData.date = date;
      }

      const response = await axiosServices.patch(`/reservations/${reservation_id}/select-waiter`, requestData);

      mutate(
        (key) => {
          if (typeof key === 'string') {
            return key.startsWith('/reservations') || key.startsWith('/waiters');
          }
          return false;
        },
        undefined,
        { revalidate: true }
      );

      return {
        success: response.data.assignment_status === 'assigned' || response.status === 200,
        message:
          response.data.assignment_status === 'assigned'
            ? 'Waiter assignment updated successfully'
            : response.data.message || 'Please select another waiter',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error changing waiter assignment:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to update waiter assignment'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { changeWaiter, isLoading };
}

// ==============================|| BATCH CHANGE WORKERS HOOK ||============================== //

interface ChangeWorkersParams {
  reservation_ids: number[];
  waiter_id: number;
}

interface ChangeWorkersResponseItem {
  reservation_id: number;
  success: boolean;
  message?: string;
}

export function useChangeWorkers() {
  const { changeWaiter } = useChangeWorker();
  const [isLoading, setIsLoading] = useState(false);

  const changeWorkers = async (params: ChangeWorkersParams): Promise<ChangeWorkersResponseItem[]> => {
    setIsLoading(true);
    try {
      const results: ChangeWorkersResponseItem[] = [];
      for (const reservationId of params.reservation_ids) {
        const res = await changeWaiter({
          reservation_id: reservationId,
          waiter_id: params.waiter_id
        });
        results.push({ reservation_id: reservationId, success: !!res?.success, message: res?.message });
      }
      return results;
    } finally {
      setIsLoading(false);
    }
  };

  return { changeWorkers, isLoading };
}

// ==============================|| RATE WAITER HOOK ||============================== //

interface RateWaiterParams {
  reservation_id?: string | number;
  qr_secret: string | number;
  waiter_id: string | number;
  stars: number;
  comment: string;
}

interface RateWaiterResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useReviewReservation() {
  const t = useTranslation();

  const rateReservation = async (params: RateWaiterParams): Promise<RateWaiterResponse> => {
    try {
      const reservationId = params.reservation_id;
      const hasReservationId =
        reservationId !== undefined &&
        reservationId !== null &&
        reservationId !== '' &&
        reservationId !== 'no-reservation' &&
        !Number.isNaN(Number(reservationId));

      const response = await axiosServices.post(`/reviews`, {
        stars: params.stars,
        comment: params.comment,
        qr_secret: params.qr_secret,
        waiter_id: params.waiter_id,
        ...(hasReservationId ? { reservation_id: Number(reservationId) } : {})
      });

      mutate(
        (key) => {
          if (typeof key === 'string') {
            return key.startsWith('/reservations/history');
          }
          return false;
        },
        undefined,
        { revalidate: true }
      );

      return {
        success: true,
        message: 'Review submitted successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error submitting review:', error);
      const errors = error?.errors;

      let errorType: string | null = null;
      if (Array.isArray(errors) && errors.length > 0) {
        const errorTitle = errors[0]?.title;
        if (
          errorTitle === 'self_review' ||
          errorTitle === 'reservation_already_reviewed' ||
          errorTitle === 'daily_limit_reached' ||
          errorTitle === 'waiter_required' ||
          errorTitle === 'invalid_waiter'
        ) {
          errorType = errorTitle;
        }
      }

      switch (errorType) {
        case 'self_review':
          return {
            success: false,
            message: t('Waiters cannot submit reviews')
          };
        case 'reservation_already_reviewed':
          return {
            success: false,
            message: t('This reservation has already been reviewed')
          };
        case 'daily_limit_reached':
          return {
            success: false,
            message: t('Daily review limit reached. Please try again tomorrow')
          };
        case 'waiter_required':
          return {
            success: false,
            message: t('Please select a waiter to continue')
          };
        case 'invalid_waiter':
          return {
            success: false,
            message: t('Selected waiter is not available for this review')
          };
        default:
          return {
            success: false,
            message: error?.message || error?.detail || t('Error submitting review')
          };
      }
    }
  };

  return { rateReservation };
}

// ==============================|| GET WAITER RESERVATIONS HOOK ||============================== //

interface GetWaiterReservationsParams {
  waiterId: number | null;
  params?: {
    status?: 'booked' | 'cancelled' | 'completed' | 'no_show';
    from?: string;
    to?: string;
  };
}

export function useGetWaiterReservations({ waiterId, params = {} }: GetWaiterReservationsParams) {
  const { status, from, to } = params;

  const getKey = () => {
    if (!waiterId) return null;

    const queryParams = new URLSearchParams();
    if (status) queryParams.set('status', status);
    if (from) queryParams.set('from', from);
    if (to) queryParams.set('to', to);

    const queryString = queryParams.toString();
    return `${endpoints.waiterReservations}/${waiterId}/reservations${queryString ? `?${queryString}` : ''}`;
  };

  const { data, error, isLoading, mutate } = useSWR(getKey, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 5000,
    focusThrottleInterval: 10000,
    errorRetryInterval: 5000,
    errorRetryCount: 3
  });

  const reservations = useMemo(() => {
    if (!data) return [];
    return data?.data || [];
  }, [data]);

  return {
    reservations,
    isLoading,
    error,
    refetch: () => mutate(),
    isEmpty: !isLoading && reservations.length === 0
  };
}

// ==============================|| GET WORKERS RESERVATIONS HOOK ||============================== //

interface GetWorkersReservationsParams {
  restaurantId: number | null;
  waiterId?: number | null;
  params?: {
    status?: 'booked' | 'cancelled' | 'completed' | 'no_show';
    from?: string;
    to?: string;
  };
}

export function useGetWorkersReservations({ restaurantId, waiterId, params = {} }: GetWorkersReservationsParams) {
  const { status, from, to } = params;

  const getKey = () => {
    if (!restaurantId) return null;

    const queryParams = new URLSearchParams();
    if (waiterId) queryParams.set('waiter_id', waiterId.toString());
    if (status) queryParams.set('status', status);
    if (from) queryParams.set('from', from);
    if (to) queryParams.set('to', to);

    const queryString = queryParams.toString();
    return `/restaurants/${restaurantId}/reservations${queryString ? `?${queryString}` : ''}`;
  };

  const { data, error, isLoading, mutate } = useSWR(getKey, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 5000,
    focusThrottleInterval: 10000,
    errorRetryInterval: 5000,
    errorRetryCount: 3
  });

  const reservations = useMemo(() => {
    if (!data) return [];
    return data?.data || [];
  }, [data]);

  return {
    reservations,
    isLoading,
    error,
    refetch: () => mutate(),
    isEmpty: !isLoading && reservations.length === 0
  };
}

// ==============================|| SCHEDULE WAITLIST HOOK ||============================== //

interface ScheduleWaitlistParams {
  restaurant_id: number;
  start_at: string;
  end_at: string;
  party_size: number;
  table_id?: number;
  zone_id?: number;
  skills?: number[];
  languages?: string[];
  experience_years?: string;
}

interface ScheduleWaitlistResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useScheduleWaitlist() {
  const scheduleWaitlist = async (params: ScheduleWaitlistParams): Promise<ScheduleWaitlistResponse> => {
    try {
      const response = await axiosServices.post('/schedule-waitlist', params);
      return {
        success: true,
        message: 'Subscribed to waitlist successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error subscribing to waitlist:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to subscribe to waitlist'
      };
    }
  };

  return { scheduleWaitlist };
}

export interface ScheduleWaitlistByPhoneParams {
  phone: string;
  locale: 'es' | 'en';
  restaurant_id: number;
  start_at: string;
  party_size: number;
}

export interface ScheduleWaitlistByPhoneResponse {
  success: boolean;
  status?: number;
  message: string;
}

export function useScheduleWaitlistByPhone() {
  const scheduleWaitlistByPhone = async (params: ScheduleWaitlistByPhoneParams): Promise<ScheduleWaitlistByPhoneResponse> => {
    try {
      const response = await axiosServices.post('/schedule-waitlist/by-phone', params);

      return {
        success: true,
        status: response.status,
        message: 'Subscribed to waitlist successfully'
      };
    } catch (error: any) {
      const status = typeof error?._status === 'number' ? error._status : undefined;

      return {
        success: false,
        status,
        message: error?.message || 'Failed to subscribe to waitlist'
      };
    }
  };

  return { scheduleWaitlistByPhone };
}
